import { useMsal } from "@azure/msal-react";
import {
    Box,
    Button,
    Flex,
    Group,
    Loader,
    Modal,
    MultiSelect,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    MantineReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_SortingState,
    useMantineReactTable,
} from "mantine-react-table";
import { useMemo, useRef, useState } from "react";
import { DatasourceBadges, RoleIdBadge } from "src/components/adminBadges";
import type { AccessPolicy } from "src/db";
import { useAuthSearchPagination } from "src/hooks/useAuthSearchPagination";
import { deleteAPI, getAPI, postAPI } from "src/utils/fetch";
import { useAdmin } from "src/utils/useAdmin";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

const SNAKE_CASE_ROLE_ID = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/;

function validateSnakeCaseRoleId(value: string): string | null {
    if (!value) return "Role ID is required";
    if (!SNAKE_CASE_ROLE_ID.test(value)) {
        return "Role ID must be lowercase snake_case (e.g., name_name_name)";
    }
    return null;
}

export type Role = {
    id: string;
    label?: string;
    description: string;
    datasources: Array<Datasources>;
};

export type Datasource = {
    id: string;
    product_lines?: string[];
};

export type Datasources = {
    id: string;
    description: string;
    datasources: Datasource[];
};

interface MutationParams {
    data: Partial<Role>;
}

const useRoleOperations = () => {
    const _isAdmin = useAdmin();
    const { instance } = useMsal();

    const createRole = async ({ data }: MutationParams) => {
        const response = await postAPI({
            url: `${BACKEND_URL}/v1/auth/roles`,
            instance,
            payload: data,
        });

        if (!response.ok) {
            throw new Error("Failed to update user");
        }

        return response.json();
    };

    const deleteRole = async ({ data }: MutationParams) => {
        const response = await deleteAPI({
            url: `${BACKEND_URL}/v1/auth/roles?role_id=${data.id}`,
            instance,
        });

        if (!response.ok) {
            throw new Error("Failed to delete role");
        }

        return response.json();
    };

    return {
        createRole,
        deleteRole,
    };
};

const ManageRoles = () => {
    const isAdmin = useAdmin();
    const { instance } = useMsal();
    const queryClient = useQueryClient();

    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "id", desc: false }]);

    const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
        useAuthSearchPagination<Role>({
            queryKey: ["roles"],
            path: "v1/auth/roles/search",
            columnFilters,
            globalFilter,
            globalFilterKey: "id",
            enabled: isAdmin,
        });

    // Define the response type for datasources
    type DatasourceResponse = {
        id: string;
        name: string;
        description: string;
        product_line_filter: string[];
        access_policy: AccessPolicy;
    };

    const { data: datasourcesData, isLoading: datasourcesLoading } = useQuery<DatasourceResponse[]>(
        {
            queryKey: ["auth-datasources"],
            queryFn: async () => {
                const response = await getAPI({
                    url: `${BACKEND_URL}/v1/auth/datasources`,
                    instance,
                });
                const jsonData = await response.json();
                if (Array.isArray(jsonData)) return jsonData;
                if (Array.isArray(jsonData?.data)) return jsonData.data;
                return [];
            },
            enabled: isAdmin,
        },
    );

    const { createRole, deleteRole } = useRoleOperations();

    const fetchedRoles = useMemo(() => {
        if (!data?.pages) return [];

        const seen = new Set<string>();
        return data.pages
            .flatMap((page) => page.hits.hits)
            .filter((hit) => {
                if (seen.has(hit._id)) return false;
                seen.add(hit._id);
                return true;
            })
            .map((hit) => hit._source);
    }, [data]);

    const totalRoles = data?.pages[0]?.hits.total.value ?? fetchedRoles.length;

    const datasourcesOptions = useMemo(() => {
        const datasources = Array.isArray(datasourcesData) ? datasourcesData : [];

        // Only role-managed datasources can be bound to roles (access_policy === "role").
        // Synced / always-on corpora are excluded from the picker.
        return datasources
            .filter((datasource) => datasource.access_policy === "role")
            .map((datasource: DatasourceResponse) => ({
                value: datasource.id,
                label: `${datasource.id} - ${datasource.name || ""}`,
            }));
    }, [datasourcesData]);

    const datasourceDescriptions = useMemo(() => {
        const datasources = Array.isArray(datasourcesData) ? datasourcesData : [];

        return Object.fromEntries(
            datasources.map((datasource) => [
                datasource.id,
                datasource.description || datasource.name || "",
            ]),
        );
    }, [datasourcesData]);

    const columns = useMemo<MRT_ColumnDef<Role>[]>(
        () => [
            {
                accessorKey: "id",
                header: "ID",
                accessorFn: (row) => (row ? row.id?.toString() : "") ?? "",
                Cell: ({ row }) => (
                    <RoleIdBadge id={row.original.id} description={row.original.description} />
                ),
            },
            {
                accessorKey: "label",
                header: "Label / Group",
                accessorFn: (row) => row?.label ?? "",
            },
            {
                accessorKey: "description",
                header: "Description",
                accessorFn: (row) => (row ? row.description?.toString() : "") ?? "",
            },
            {
                accessorKey: "datasources",
                header: "Datasources",
                accessorFn: (row) => {
                    if (!row?.datasources) return "";
                    return (
                        row.datasources
                            .map((obj) => obj?.id)
                            .filter(Boolean)
                            .join(", ") || ""
                    );
                },
                Cell: ({ row }) => (
                    <DatasourceBadges
                        items={row.original.datasources?.map((ds) => ds.id).filter(Boolean) ?? []}
                        descriptions={{
                            ...datasourceDescriptions,
                            ...Object.fromEntries(
                                (row.original.datasources ?? [])
                                    .filter((ds) => ds.description)
                                    .map((ds) => [ds.id, ds.description]),
                            ),
                        }}
                    />
                ),
            },
        ],
        [datasourceDescriptions],
    );

    const tableContainerRef = useRef<HTMLDivElement>(null);

    const fetchMoreOnBottomReached = (containerRefElement?: HTMLDivElement) => {
        if (containerRefElement) {
            const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
            if (
                scrollHeight - scrollTop - clientHeight < 400 &&
                !isFetchingNextPage &&
                hasNextPage
            ) {
                fetchNextPage();
            }
        }
    };

    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [modalOpened, setModalOpened] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            if (!selectedRole) {
                throw new Error("No role selected");
            }

            await deleteRole({
                data: {
                    id: selectedRole.id,
                },
            });

            notifications.show({
                title: "Success",
                message: "Role deleted successfully",
                color: "green",
            });
            setModalOpened(false);
            setCreateRoleModalOpened(false);

            await new Promise((resolve) => setTimeout(resolve, 1000));
            await queryClient.invalidateQueries({ queryKey: ["roles"] });
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to delete role",
                color: "red",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const [createRoleModalOpened, setCreateRoleModalOpened] = useState(false);
    const [isCreatingRole, setIsCreatingRole] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const createRoleForm = useForm({
        initialValues: {
            id: "",
            label: "",
            description: "",
            datasources: [] as string[],
        },
        validate: {
            id: validateSnakeCaseRoleId,
            description: (value) => (!value ? "Description is required" : null),
        },
    });

    const handleOpenCreateModal = () => {
        setIsEditMode(false);
        createRoleForm.reset();
        setCreateRoleModalOpened(true);
    };

    const handleOpenEditModal = (role: Role) => {
        if (isLoading) {
            notifications.show({
                title: "Loading",
                message: "Please wait while roles are being loaded...",
                loading: true,
            });
            return;
        }
        setIsEditMode(true);
        setSelectedRole(role);
        const datasourceIds = role.datasources?.map((ds) => ds.id) || [];

        createRoleForm.setValues({
            id: role.id,
            label: role.label ?? "",
            description: role.description,
            datasources: datasourceIds,
        });
        setCreateRoleModalOpened(true);
    };

    const handleCreateOrUpdateRole = async (values: typeof createRoleForm.values) => {
        setIsCreatingRole(true);
        try {
            const _url = `${BACKEND_URL}/v1/auth/roles`;
            const datasourcesPayload = values.datasources.map((id) => ({ id }));
            const data = await createRole({
                data: {
                    id: values.id,
                    label: values.label || undefined,
                    description: values.description,
                    // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                    datasources: datasourcesPayload as any,
                },
            });

            if (!data) {
                throw new Error(`Failed to ${isEditMode ? "update" : "create"} role`);
            }
            notifications.show({
                title: "Success",
                message: `Role ${isEditMode ? "updated" : "created"} successfully`,
                color: "green",
            });
            setCreateRoleModalOpened(false);
            createRoleForm.reset();

            await new Promise((resolve) => setTimeout(resolve, 1000));
            await queryClient.invalidateQueries({ queryKey: ["roles"] });
        } catch (error) {
            notifications.show({
                title: "Error",
                message:
                    error instanceof Error
                        ? error.message
                        : `Failed to ${isEditMode ? "update" : "create"} role`,
                color: "red",
            });
        } finally {
            setIsCreatingRole(false);
        }
    };

    const table = useMantineReactTable({
        // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        columns: columns as any,
        layoutMode: "grid",
        data: fetchedRoles ?? [],
        enableColumnOrdering: true,
        enablePinning: true,
        enableBottomToolbar: false,
        renderToolbarAlertBannerContent: () => <></>,
        renderTopToolbarCustomActions: () => (
            <Flex align="center" gap="md">
                <Text size="sm" c="dimmed">
                    {`Total: ${totalRoles.toLocaleString()}, (Showing ${table.getFilteredRowModel().rows.length.toLocaleString()})`}
                </Text>
                {isFetching && <Loader size="xs" />}
            </Flex>
        ),
        enableColumnResizing: true,
        enableFilters: true,
        enablePagination: false,
        enableRowNumbers: true,
        enableClickToCopy: false,
        getRowId: (row) => row.id,
        mantineToolbarAlertBannerProps: {
            style: {
                display: "none",
                background: "#f0f0f0",
                fontWeight: "bold",
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        enableSelectAll: false,
        selectAllMode: "page",
        enableGrouping: true,
        enableDensityToggle: false,
        enableStickyHeader: true,
        mantineTableContainerProps: {
            ref: tableContainerRef,
            style: { maxHeight: "calc(100vh - 280px)" },
            onScroll: (event) => fetchMoreOnBottomReached(event.currentTarget),
        },
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: () => handleOpenEditModal(row.original),
            style: {
                cursor: isLoading ? "wait" : "pointer",
                opacity: isLoading ? 0.7 : 1,
            },
        }),
        state: {
            isLoading: isLoading || isLoading,
            showProgressBars: isFetchingNextPage,
            columnFilters,
            globalFilter,
            sorting,
        },
        initialState: {
            showGlobalFilter: true,
            density: "xs",
            showColumnFilters: true,
        },
    });

    return (
        <>
            <Flex px="xl" pb="xl" direction="column" gap="md">
                <Flex gap="md" align="center" justify="space-between">
                    <Title lh={2} c="purple.8" order={2} tt="uppercase">
                        Manage Roles
                    </Title>
                    <Button onClick={handleOpenCreateModal} variant="filled">
                        Create New Role
                    </Button>
                </Flex>
            </Flex>

            <MantineReactTable table={table} />

            <Modal
                closeButtonProps={{ "aria-label": "Close modal" }}
                opened={createRoleModalOpened}
                onClose={() => {
                    setCreateRoleModalOpened(false);
                    createRoleForm.reset();
                }}
                title={isEditMode ? `Edit Role ${createRoleForm.values.id}` : "Create New Role"}
                size="md"
            >
                <Box component="form" onSubmit={createRoleForm.onSubmit(handleCreateOrUpdateRole)}>
                    <Stack gap="md">
                        {isEditMode ? (
                            <Box>
                                <Text size="sm" fw={500} mb={4}>
                                    Role ID
                                </Text>
                                <RoleIdBadge
                                    id={createRoleForm.values.id}
                                    description={createRoleForm.values.description}
                                />
                            </Box>
                        ) : (
                            <TextInput
                                required
                                label="Role ID"
                                placeholder="e.g., admin_role"
                                value={createRoleForm.values.id}
                                error={createRoleForm.errors.id}
                                onChange={(e) => {
                                    const value = e.currentTarget.value
                                        .toLowerCase()
                                        .replace(/[^a-z0-9_]/g, "");
                                    createRoleForm.setFieldValue("id", value);
                                }}
                                onBlur={() => createRoleForm.validateField("id")}
                            />
                        )}

                        <TextInput
                            label="Label / Group"
                            placeholder="e.g., Level 1"
                            {...createRoleForm.getInputProps("label")}
                        />

                        <Textarea
                            required
                            label="Description"
                            placeholder="Describe the purpose of this role"
                            minRows={3}
                            {...createRoleForm.getInputProps("description")}
                        />
                        <MultiSelect
                            label="Datasources"
                            placeholder="Select datasource"
                            data={datasourcesOptions}
                            {...createRoleForm.getInputProps("datasources")}
                            searchable
                            clearable
                            nothingFoundMessage="No datasource found"
                            disabled={isLoading || datasourcesLoading}
                            clearButtonProps={{ "aria-label": "Clear selected values" }}
                        />
                        {createRoleForm.values.datasources.length > 0 && (
                            <DatasourceBadges
                                items={createRoleForm.values.datasources}
                                descriptions={datasourceDescriptions}
                            />
                        )}
                        {isEditMode ? (
                            <Group justify="flex-end" mt="md">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setCreateRoleModalOpened(false);
                                        createRoleForm.reset();
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    color="red"
                                    onClick={() => setModalOpened(true)}
                                >
                                    Delete Role
                                </Button>
                                <Button type="submit" color="green" loading={isCreatingRole}>
                                    Update Role
                                </Button>
                            </Group>
                        ) : (
                            <Button type="submit" loading={isCreatingRole} fullWidth>
                                Create Role
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Modal>

            <Modal
                opened={modalOpened}
                onClose={() => {
                    setModalOpened(false);
                }}
                title={`Delete Role ${selectedRole?.id || ""}?`}
                size="md"
            >
                <Box>
                    <Text mb="md">
                        Are you sure you want to delete this role? This action cannot be undone.
                    </Text>
                    {selectedRole && (
                        <Group gap="xs" mb="md">
                            <RoleIdBadge
                                id={selectedRole.id}
                                description={selectedRole.description}
                            />
                            <DatasourceBadges
                                items={
                                    selectedRole.datasources?.map((ds) => ds.id).filter(Boolean) ??
                                    []
                                }
                                descriptions={{
                                    ...datasourceDescriptions,
                                    ...Object.fromEntries(
                                        (selectedRole.datasources ?? [])
                                            .filter((ds) => ds.description)
                                            .map((ds) => [ds.id, ds.description]),
                                    ),
                                }}
                            />
                        </Group>
                    )}
                    <Flex gap="md" justify="flex-end">
                        <Button variant="outline" onClick={() => setModalOpened(false)}>
                            Cancel
                        </Button>
                        <Button color="red" loading={isSubmitting} onClick={handleSubmit}>
                            Delete
                        </Button>
                    </Flex>
                </Box>
            </Modal>
        </>
    );
};

export default ManageRoles;
